<a
  class="btn btn-ghost btn-sm -ml-3 mb-2 text-muted-foreground"
  routerLink="/applications"
  [queryParams]="backTab"
>
  <app-icon name="arrow-left" />
  Back to applications
</a>

@if (!canManage) {
  <div class="card">
    <div class="empty">Applications are managed by the managers of your main agency.</div>
  </div>
} @else if (!loaded) {
  <div class="mb-6 flex items-center gap-4">
    <div class="skeleton h-14 w-14"></div>
    <div class="flex flex-col gap-2">
      <div class="skeleton h-6 w-48"></div>
      <div class="skeleton h-4 w-72"></div>
    </div>
  </div>
  <div class="two-one">
    <div class="card flex flex-col gap-3 p-6">
      <div class="skeleton h-5 w-1/4"></div>
      <div class="skeleton h-6 w-2/3"></div>
    </div>
    <div class="card flex flex-col gap-3 p-6">
      <div class="skeleton h-5 w-1/3"></div>
      <div class="skeleton h-4 w-full"></div>
    </div>
  </div>
} @else if (!app) {
  <div class="card">
    <div class="empty">
      This application was not found, or it is no longer offered to your agency.
      <div class="mt-4">
        <a class="btn btn-outline btn-sm" routerLink="/applications">Back to applications</a>
      </div>
    </div>
  </div>
} @else {
  <div class="page-head">
    <div class="flex min-w-0 items-start gap-4">
      <div class="logo h-14 w-14 text-base" [class.logo-muted]="!app.logo_url">
        @if (app.logo_url) {
          <img [src]="app.logo_url" [alt]="app.name + ' logo'" />
        } @else {
          {{ initials(app.name) }}
        }
      </div>
      <div class="min-w-0">
        <div class="flex flex-wrap items-center gap-3">
          <h1 class="title">{{ app.name }}</h1>
          <span [class]="'badge ' + badge(app).cls">{{ badge(app).label }}</span>
        </div>
        @if (app.description) {
          <p class="desc max-w-[720px]">{{ app.description }}</p>
        }
      </div>
    </div>
    <div class="flex flex-wrap gap-2">
      @if (app.url) {
        <a class="btn btn-outline" [href]="app.url" target="_blank" rel="noopener">
          Visit website
          <app-icon name="arrow-up-right" />
        </a>
      }
      @if (!app.connection) {
        <button type="button" class="btn btn-primary" (click)="modalVisible = true">
          Request access
        </button>
      } @else if (canDrop(app)) {
        <button
          type="button"
          class="btn btn-outline text-destructive"
          (click)="modalVisible = true"
        >
          {{ toggleLabel(app) }}
        </button>
      }
    </div>
  </div>

  <div class="two-one">
    <div class="flex flex-col gap-6">
      <!-- Access -->
      @let permissions = app.permissions | filterPermissions;
      <div class="card">
        <div class="card-h block">
          <h2 class="card-t">Access</h2>
          <p class="card-d">
            @if (app.connection?.status === 'active') {
              What this application can do with your airline content.
            } @else {
              What this application will be able to do once it is enabled.
            }
          </p>
        </div>
        <div class="card-c flex flex-col gap-5">
          <div class="flex flex-col gap-2">
            <span class="label">
              {{ app.connection?.status === 'active' ? 'Permissions' : 'Requested permissions' }}
            </span>
            @if (permissions.length) {
              <div class="flex flex-wrap gap-1.5">
                @for (permission of permissions; track permission) {
                  <span class="badge badge-secondary">{{ permission }}</span>
                }
              </div>
            } @else {
              <span class="text-sm text-muted-foreground">None</span>
            }
          </div>
        </div>
      </div>

      <!-- Credentials (enabled apps only) -->
      @if (app.connection?.status === 'active') {
        <div class="card">
          <div class="card-h block">
            <h2 class="card-t">Credentials</h2>
            <p class="card-d">Keep these secret — anyone holding them can act for your agency.</p>
          </div>
          <div class="card-c flex flex-col gap-4">
            @if (apiKey) {
              <div class="field">
                <span class="label">AGW API key</span>
                <div class="flex items-center gap-2">
                  <code class="code min-w-0 flex-1 truncate py-2 text-xs">
                    {{ revealed ? apiKey : mask(apiKey) }}
                  </code>
                  <button
                    type="button"
                    class="btn btn-outline btn-icon"
                    [attr.aria-label]="revealed ? 'Hide API key' : 'Reveal API key'"
                    (click)="revealed = !revealed"
                  >
                    <app-icon [name]="revealed ? 'eye-off' : 'eye'" />
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline btn-icon"
                    aria-label="Copy API key"
                    (click)="copy(apiKey)"
                  >
                    <app-icon name="copy" />
                  </button>
                </div>
              </div>
            } @else {
              <p class="m-0 text-sm text-muted-foreground">
                No credentials have been issued for this application.
              </p>
            }
          </div>
        </div>
      } @else if (app.connection?.status === 'pending') {
        <div class="card">
          <div class="card-h block">
            <h2 class="card-t">Credentials</h2>
            <p class="card-d">
              An API key is issued once the application’s developer approves your request.
            </p>
          </div>
        </div>
      } @else if (app.connection?.status === 'suspended') {
        <div class="card">
          <div class="card-h block">
            <h2 class="card-t">Credentials</h2>
            <p class="card-d">
              The application’s developer has suspended your access. Your API key is kept and works
              again once they resume it.
            </p>
          </div>
        </div>
      }
    </div>

    <!-- Details -->
    <div class="card self-start">
      <div class="card-h"><h2 class="card-t">Details</h2></div>
      <div class="card-c">
        <dl class="dl">
          <dt>Status</dt>
          <dd>
            <span
              class="status"
              [class.text-muted-foreground]="app.connection?.status !== 'active'"
            >
              <span class="dot" [class.dot-on]="app.connection?.status === 'active'"></span>
              {{ badge(app).label }}
            </span>
          </dd>
          @if (app.agw_id) {
            <dt>App ID</dt>
            <dd>
              <span class="code">{{ app.agw_id }}</span>
            </dd>
          }
          @if (app.url) {
            <dt>Website</dt>
            <dd>
              <a [href]="app.url" target="_blank" rel="noopener" [title]="app.url">
                {{ app.url }}
              </a>
            </dd>
          }
          @if (app.technical_email) {
            <dt>Technical contact</dt>
            <dd [title]="app.technical_email">{{ app.technical_email }}</dd>
          }
          @if (app.connection?.created_at) {
            <dt>Requested</dt>
            <dd>{{ app.connection?.created_at | date: 'mediumDate' }}</dd>
          }
        </dl>
      </div>
    </div>
  </div>
}

<app-confirm-dialog
  [open]="modalVisible"
  [title]="confirmation.title"
  [confirmLabel]="app?.connection ? toggleLabel(app!) : 'Request access'"
  [destructive]="!!app?.connection"
  [busy]="saving"
  (confirm)="confirmToggle()"
  (cancel)="modalVisible = false"
>
  {{ confirmation.message }}
</app-confirm-dialog>
